import styles from "./Vehicules.module.scss";
import { Content, ContentFixed, ContentScroll } from "../../common/Content";
import { HeaderMenu } from "../../common/HeaderMenu";
import React, { useEffect, useRef, useState } from "react";
import { FabButton } from "../../common/FabButton";
import { MODE_CREATE, MODE_EDIT } from "../../codes";
import Card from "../../common/Card";
import { useSearchParams } from "react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  apiCreateVehiculeFile,
  apiDeleteVehicule,
  apiDeleteVehiculeFile,
  apiGetVehicules
} from "../../api/ApiVehicules";
import { AppModal, DeleteModal } from "../../common/AppModal";
import { ItemActions } from "../../common/ItemActions";
import VehiculeEditor from "./VehiculeEditor";
import useToast from "../../util/useToast";
import { useDebounce } from "use-debounce";
import Form from "react-bootstrap/esm/Form";
import { AppPagination } from "../../common/AppPagination";
import clsx from "clsx";
import { useFileUploader } from "../../util/useFileUploader";
import { PhotoViewer } from "../../common/PhotoViewer";
import { renderDescription } from "../../util/util";

const Vehicules = () => {
  const { showToastSuccess } = useToast();

  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInputValue, setSearchInputValue] = useState(searchParams.get("filter") || "");
  const [debouncedFilter] = useDebounce(searchInputValue, 200);

  const [vehiculeEditor, setVehiculeEditor] = useState({ show: false, mode: MODE_CREATE, vehicule: {} });
  const [deleteVehiculeModal, setDeleteVehiculeModal] = useState({ show: false, vehicule: null });
  const [deleteFileModal, setDeleteFileModal] = useState({ show: false, vehicule: null, file: null });
  const [fileVehicule, setFileVehicule] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [photosPerRow, setPhotosPerRow] = useState(6);
  const [displayedPhotoVehiculeId, setDisplayedPhotoVehiculeId] = useState(null);
  const [displayedPhoto, setDisplayedPhoto] = useState(null);
  const [photos, setPhotos] = useState([]);

  const fileInputRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      if (width < 581) {
        setPhotosPerRow(3);
      } else if (width < 721) {
        setPhotosPerRow(4);
      } else if (width < 861) {
        setPhotosPerRow(5);
      } else {
        setPhotosPerRow(6);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const filter = debouncedFilter.trim();

    if (filter !== (searchParams.get("filter") || "")) {
      setSearchParams(filter ? { filter } : {});
    }
  }, [debouncedFilter]);

  const queryParams = { size: 50, page: +searchParams.get("page") || 0 };

  if (searchParams.get("filter")) {
    queryParams.filter = searchParams.get("filter");
  }

  const vehiculesQuery = useQuery({
    ...apiGetVehicules(queryParams),
    enabled: false
  });

  const deleteVehiculeMutation = useMutation(apiDeleteVehicule());
  const createFileMutation = useMutation(apiCreateVehiculeFile());
  const deleteFileMutation = useMutation(apiDeleteVehiculeFile());

  const { uploadFiles, progress, total } = useFileUploader(
    (file, content) => createFileMutation.mutateAsync({
      vehiculeId: fileVehicule.id,
      data: { name: file.name, content }
    })
  );

  const handleFileChange = async (event) => {
    setUploadingFiles(true);

    uploadFiles(event.target.files)
      .then(() => vehiculesQuery.refetch())
      .finally(() => {
        event.target.value = "";

        setUploadingFiles(false);
      });
  };

  const deleteFile = () => {
    return deleteFileMutation.mutateAsync({ vehiculeId: deleteFileModal.vehicule.id, fileId: deleteFileModal.file.id })
      .then(() => vehiculesQuery.refetch())
      .then(() => {
        setDeleteFileModal({ show: false });

        showToastSuccess("Fichier supprimé");
      });
  };

  const vehicules = vehiculesQuery.data.content;
  const totalPages = vehiculesQuery.data.page.totalPages;
  const totalElements = vehiculesQuery.data.page.totalElements;

  const actions = [
    {
      icon: "add",
      label: "Ajouter un véhicule",
      onClick: () => setVehiculeEditor({ show: true, mode: MODE_CREATE, vehicule: {} })
    },
  ];

  const confirmDeleteFile = (vehicule, file) => e => {
    e.preventDefault();

    setDeleteFileModal({ show: true, file, vehicule });
  };

  return <Content>
    <ContentFixed>
      <HeaderMenu title={"Véhicules"} />
    </ContentFixed>

    <ContentScroll paddingTop withFab>
      <div className={styles.vehiculesLayout}>
        <div className={styles.searchBar}>
          <Form.Control
            type="text"
            className={styles.searchInput}
            placeholder="Chercher"
            value={searchInputValue}
            onChange={e => setSearchInputValue(e.target.value)}
          />
        </div>

        <Card>
          {vehicules.length === 0 && <div className={styles.vehiculesEmpty}>Pas de véhicules</div>}

          {vehicules.map(vehicule => {
            const vehiculeActions = [
              {
                type: "item",
                icon: "edit",
                label: "Modifier",
                onClick: () => setVehiculeEditor(() => ({ show: true, mode: MODE_EDIT, vehicule }))
              },
              {
                type: "item",
                icon: "upload_file",
                label: "Ajouter des fichiers",
                onClick: () => {
                  setFileVehicule(vehicule);

                  fileInputRef.current.click();
                }
              },
              { type: "divider" },
              {
                type: "item",
                icon: "delete",
                label: "Supprimer",
                onClick: () => setDeleteVehiculeModal({ show: true, vehicule })
              },
            ];

            const files = vehicule.files.filter(f => !f.pictureWidth);
            const photos = vehicule.files.filter(f => !!f.pictureWidth);

            const remainder = photos.length % photosPerRow;
            const spacersCount = remainder === 0 ? 0 : photosPerRow - remainder;

            return <div key={vehicule.id} className={styles.vehicule}>
              <div className={styles.view}>
                <div className={styles.name}>{vehicule.name}</div>

                {vehicule.description && <div className={styles.description}
                                              dangerouslySetInnerHTML={{ __html: renderDescription(vehicule.description) }} />}

                {(files.length > 0) && <div className={styles.files}>
                  {files.sort((a, b) => a.name.localeCompare(b.name)).map(file => {
                    const extension = file.name.split(".").pop()?.toLowerCase() || "unknown";

                    return <>
                      <a key={file.id}
                         className={styles.file}
                         href={process.env.REACT_APP_API_URL + "/public/vef/" + file.fileKey}
                         download={file.name}
                         title={file.name}
                         target="_blank">
                        <span className={clsx(styles.extension, styles[extension])}>{extension}</span>
                        <span className={styles.name}>{file.name}</span>
                        <span className={styles.delete} onClick={confirmDeleteFile(vehicule, file)}>
                          <span className={clsx("material-icons", styles.deleteIcon)}>close</span>
                        </span>
                      </a>
                    </>;
                  })}
                </div>}

                {(photos.length > 0) && <div className={styles.photos}>
                  {photos.sort((a, b) => a.name.localeCompare(b.name)).map(photo => {
                    return <span className={styles.photo} key={photo.id} onClick={() => {
                      setPhotos(photos);
                      setDisplayedPhoto(photo);
                      setDisplayedPhotoVehiculeId(vehicule.id);
                    }}>
                      <img src={process.env.REACT_APP_API_URL + "/public/vep/" + photo.fileKey + "?w=200&h=200"} />
                    </span>;
                  })}

                  {Array
                    .from({ length: spacersCount }, (_, x) => x)
                    .map(d => <div key={d} className={styles.photo}></div>)}
                </div>}
              </div>

              <ItemActions actions={vehiculeActions} />
            </div>;
          })}
        </Card>

        <Card padding>
          <AppPagination
            itemName="véhicule"
            page={queryParams.page}
            totalElements={totalElements}
            totalPages={totalPages}
          />
        </Card>
      </div>

      <FabButton icon="more_vert" actions={actions} />

      <VehiculeEditor
        show={vehiculeEditor.show}
        mode={vehiculeEditor.mode}
        hide={() => setVehiculeEditor(prev => ({ ...prev, show: false }))}
        vehicule={vehiculeEditor.vehicule}
        onSubmit={() => vehiculesQuery.refetch()}
      />

      <PhotoViewer
        photos={photos}
        photo={displayedPhoto}
        setPhoto={setDisplayedPhoto}
        reloadPhotos={() => vehiculesQuery.refetch()}
        onDelete={photoId => deleteFileMutation.mutateAsync({ vehiculeId: displayedPhotoVehiculeId, fileId: photoId })}
        publicPath="vep"
      />

      <DeleteModal
        show={deleteVehiculeModal.show}
        title="Supprimer le véhicule"
        onCancel={() => setDeleteVehiculeModal({ show: false })}
        onSubmit={() => {
          return deleteVehiculeMutation.mutateAsync({ id: deleteVehiculeModal.vehicule.id }).then(() => {
            vehiculesQuery.refetch().then(() => {
              showToastSuccess("Véhicule supprimé");

              setDeleteVehiculeModal(prev => ({ ...prev, show: false }));
            });
          });
        }}
        cancelLabel="Annuler"
        submitLabel="Supprimer"
      >
        Etes-vous sûr de vouloir supprimer ce véhicule ?
      </DeleteModal>

      <DeleteModal
        className={styles.deleteFileModal}
        size={"sm"}
        show={deleteFileModal.show}
        title="Supprimer le fichier"
        onCancel={() => setDeleteFileModal({ show: false })}
        onSubmit={deleteFile}
        cancelLabel="Annuler"
        submitLabel="Supprimer"
      >
        Etes-vous sûr de vouloir supprimer ce fichier ?
      </DeleteModal>

      <AppModal
        size="sm"
        show={uploadingFiles}
        title="Envoi en cours"
      >
        <p>Veuillez patienter pendant l'envoi de vos fichiers ({progress} / {total}).</p>

        <div className={styles.fileProgressBar}>
          <div className={styles.fileProgressBarValue}></div>
        </div>
      </AppModal>

      <input
        className={styles.inputFile}
        type="file"
        ref={fileInputRef}
        name="files"
        multiple="multiple"
        onChange={handleFileChange}
      />
    </ContentScroll>
  </Content>;
};

export default Vehicules;
